import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Layout, Spin, Result, Button } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PublicForm from './PublicForm';
import { getPublicForm, submitPublicFormRequest, getPublicNotificationTemplates } from '../../services/publicFormServices';
import { FORM_LANGUAGES, DEFAULT_LANGUAGE } from '../../utils/formTranslations';
import './public-form.css';
import moment from 'moment';

const { Content } = Layout;

const PublicFormPage = () => {
    const { shareToken } = useParams();
    const [formData, setFormData] = useState(null);
    const [formDefaultValues, setFormDefaultValues] = useState(null)
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [notificationTemplates, setNotificationTemplates] = useState([])
    const [documentId, setDocumentId] = useState("")
    const [tenantId, setTenantId] = useState("")
    const [caseId, setCaseId] = useState("")
    const [searchParams] = useSearchParams();
    const { t, i18n } = useTranslation();
  const type = searchParams.get('type');
    const langParam = searchParams.get('lang');

    // The language chosen when the link was created rides on the share record
    // (`language`), or as `?lang=` for links copied straight out of the builder.
    // It is handed to PublicForm explicitly rather than read back off i18n: App.jsx
    // resets the global locale to userInfo.defaultLanguage once a signed-in
    // viewer's profile loads, which would otherwise overwrite it.
    const linkLanguage = formData?.language || formData?.shareLink?.language || langParam;
    const isSupportedLanguage = FORM_LANGUAGES.some((lang) => lang.code === linkLanguage);
    const formLanguage = isSupportedLanguage ? linkLanguage : DEFAULT_LANGUAGE;

    // The tenantId state is only ever populated from a draft-request response, and
    // that draft is never created for ongoing links — leaving every ongoing upload
    // (attachments, images and the submission print) sending an empty tenant. The
    // form payload already carries the tenant, so fall back to it.
    const effectiveTenantId = tenantId || formData?.tenantId || formData?.workflow?.tenantId || "";

    // Anonymous recipients have no profile language, so align the app locale too and
    // the surrounding UI strings match the form.
    useEffect(() => {
        if (!isSupportedLanguage || i18n.language === linkLanguage) return;
        i18n.changeLanguage(linkLanguage);
    }, [isSupportedLanguage, linkLanguage, i18n]);

    const hasAttachmentElement = (items = []) => {
        return items.some((item) => {
            if (item?.formType === 'attachment' || item?.formType === "imageForm") {
                return true;
            }

            if (Array.isArray(item?.children) && item.children.length > 0) {
                return hasAttachmentElement(item.children);
            }

            return false;
        });
    };

    const createDraftRequest = async (formData={}) => {
        let nodeId = formData?.workflow?.node?.find(node => node.formData?.formId === formData.formId)?.id
        let postData = {
            spaceId: formData.workflow?.spaceId,
            spaceName: formData.spaceData?.name,
            workflowId: formData?.workflow?.workflowId,
            createdBy: {},
            createdOn: moment().utc(),
            currentCycle: "1",
            status: "draft",
            value: {},
            dueDate: "",
            workflow: [],
            // The draft carries no print yet — the submission print is generated and
            // appended when the form is actually submitted against this request.
            formPrintList: [],
            originalWorkflow: { nodes: formData.workflow.node, edges: formData.workflow.edge },
            workflowConditions: formData.workflowConditions,
            formConditions: formData.formConditions,
            workflowForms: formData.items ? [{formHeader: formData.formHeader, items: formData.items, formId: formData.formId, spaceId: formData.workflow?.spaceId, tenantId: formData.workflow?.tenantId, nodeId: nodeId, workflowId: formData?.workflow?.workflowId}] : []
        }
        let postResponse = await submitPublicFormRequest(shareToken, postData)
        if (postResponse.status === 200) {
            const draftRequestId = postResponse.data?.requestId || postResponse.data?.result?.requestId
            const draftTenantId = postResponse.data?.tenantId || postResponse.data?.result?.tenantId
            const draftCaseId = postResponse.data?.caseId || postResponse.data?.result?.caseId || ""
            setDocumentId(draftRequestId)
            setTenantId(draftTenantId)
            if (draftCaseId) {
                setCaseId(draftCaseId)
            }
            return { requestId: draftRequestId, tenantId: draftTenantId, caseId: draftCaseId }
        }
        return null
    }

    // The submission print has to be uploaded against a saved request, but a draft
    // is only created upfront for forms carrying attachment widgets. Create one on
    // demand for the rest. The ids are returned (not just set) because the caller
    // needs them in the same tick, and they're held in state so a retried submit
    // reuses the draft instead of leaving an orphan behind.
    const ensureRequestRecord = async () => {
        if (documentId) {
            return { requestId: documentId, tenantId: effectiveTenantId, caseId: caseId || formData?.caseId || "" }
        }
        if (!formData) {
            return null
        }
        return await createDraftRequest(formData)
    }


    const fetchPublicForm = async () => {
        try {
            setLoading(true);
            const response = await getPublicForm(shareToken, type);

            if (response.status !== 200) {
                if (response.status === 404) {
                    setError(t('Form not found or link has expired'));
                } else if (response.status === 403) {
                    setError(t('This form is no longer accepting submissions'));
                } else {
                    setError(response.message || t('Failed to load form'));
                }
                return;
            }

            // Handle API response structure with 'result' field
            const formData = response.data?.result || response.data;
            if (!formData || formData.message) {
                setError(formData?.message || t('Unable to load form'));
                return
            }
            setFormData(formData);
            setFormDefaultValues(formData?.defaultValues || null);
            if(formData.requestId) {
                setDocumentId(formData.requestId)
            }
            if (formData.caseId || formData.request?.caseId || formData.requestDetail?.caseId) {
                setCaseId(formData.caseId || formData.request?.caseId || formData.requestDetail?.caseId)
            }
            if(type !== "ongoing" && hasAttachmentElement(formData?.items || [])) {
                await createDraftRequest(formData)
            }
        } catch (err) {
            setError(t('Failed to load form. Please check your connection.'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const loadForm = async () => {
            if (shareToken) {
                await fetchPublicForm();
            } else {
                setError(t('Invalid form link'));
                setLoading(false);
            }
        };

        loadForm();
    }, [shareToken]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSubmissionSuccess = () => {
        setIsSubmitted(true);
    };

    const handleRetry = () => {
        setError(null);
        fetchPublicForm();
    };

    const fetchSpaceNotificationTemplates = async () => {
        const notiResponse = await getPublicNotificationTemplates(shareToken, formData?.workflow?.spaceId)
        if (notiResponse?.data?.length > 0) {
            setNotificationTemplates(notiResponse.data)
        }
    }

    useEffect(() => {
        if (formData?.workflow?.spaceId) {
            fetchSpaceNotificationTemplates();
        }
    }, [formData])

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
                <div className="bg-white p-8 sm-p-4 rounded-lg shadow-lg text-center">
                    <Spin size="large" />
                    <p className="mt-4 text-gray-600">{t('Loading form...')}</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center p-4">
                <div className="w-full max-w-md">
                    <Result
                        status="error"
                        icon={<ExclamationCircleOutlined className="text-red-500" />}
                        title={error || t('Unable to Load Form')}
                        extra={[
                            <Button
                                key="retry"
                                type="primary"
                                onClick={handleRetry}
                                className="bg-gradient-to-r from-[#141bcb] to-[#0a99fe] border-none hover:from-[#0a99fe] hover:to-[#141bcb]"
                            >
                                {t('Try Again')}
                            </Button>,
                            <Button key="close" onClick={() => window.close()}>
                                {t('Close')}
                            </Button>
                        ]}
                    />
                </div>
            </div>
        );
    }

    if (isSubmitted) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center p-4">
                <div className="w-full max-w-md">
                    <Result
                        status="success"
                        title={t('Form Submitted Successfully!')}
                        subTitle={t('Thank you for your submission. You may now close this window.')}
                        extra={[
                            <Button key="close" onClick={() => window.close()}>
                                {t('Close')}
                            </Button>
                        ]}
                    />
                </div>
            </div>
        );
    }

    return (
        <Layout className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
            <Content className="p-4 public-form-page">
                <div className="mx-auto bg-white rounded-lg shadow-lg overflow-hidden public-form-card">

                    {/* Dynamic themed header */}
                    {(() => {
                        const theme = formData?.theme || {};
                        const headerColor = theme.headerColor || '#E8E8FA';
                        const showCompanyLogo = theme.showCompanyLogo && theme.companyLogoUrl;
                        const headerText = theme.headerText?.trim();
                        return (
                            <div
                                className="p-5 text-white flex items-center justify-center gap-3"
                                style={{ background: headerColor }}
                            >
                                {showCompanyLogo ? (
                                    <>
                                        <img
                                            src={theme.companyLogoUrl}
                                            alt={t('Company Logo')}
                                            style={{ height: 36, maxWidth: 140, objectFit: 'contain' }}
                                        />
                                        {headerText ? (
                                            <span className="text-lg font-semibold">{headerText}</span>
                                        ) : null}
                                    </>
                                ) : (
                                    headerText ? (
                                        <span className="text-lg font-semibold">{headerText}</span>
                                    ) : (
                                        <>
                                            <img
                                                src="/flxflow_logo.svg"
                                                alt={t('FlxFlow')}
                                                className="h-8"
                                                onError={(e) => { e.target.style.display = 'none'; }}
                                            />
                                        </>
                                    )
                                )}
                            </div>
                        );
                    })()}

                    <div className="p-8 public-form-inner">
                        <PublicForm
                            formData={formData}
                            defaultFormValues={formDefaultValues}
                            shareToken={shareToken}
                            onSubmissionSuccess={handleSubmissionSuccess}
                            notificationTemplates={notificationTemplates}
                            requestId={documentId}
                            tenantId={effectiveTenantId}
                            caseId={caseId || formData?.caseId || formData?.request?.caseId || formData?.requestDetail?.caseId || ""}
                            setDocumentId={setDocumentId}
                            ensureRequestRecord={ensureRequestRecord}
                            type={type}
                            language={formLanguage}
                        />
                    </div>

                    {/* Powered by FlxFlow — footer */}
                    <div
                        style={{
                            padding: '10px 16px',
                            textAlign: 'center',
                            borderTop: '1px solid #f0f0f0',
                            fontSize: 12,
                            color: '#9ca3af',
                        }}
                    >
                        {t('Powered by')}{' '}
                        <span style={{ fontWeight: 600, color: '#6b7280' }}>{t('FlxFlow')}</span>
                    </div>
                </div>
            </Content>
        </Layout>
    );
};

export default PublicFormPage; 
